import React, { createContext, useContext, useCallback, useState, useMemo } from 'react';
import { walletService } from '../domain/services/walletService';
import { Transaction } from '../domain/types';
import { logger } from '../infrastructure/logger';
import { useAuth } from './auth';
import { useToast } from './ToastContext';
import { userRepository } from '../data/userRepository';

interface WalletContextType {
  // Balance state
  balance: number;
  pendingBalance: number;
  transactions: Transaction[];
  loading: boolean;

  // Balance operations
  deductWalletBalance: (amount: number) => Promise<boolean>;
  addWalletBalance: (amount: number) => Promise<boolean>;
  topUp: (amount: number) => Promise<string>;

  // Refresh
  refreshWallet: () => Promise<void>;
}

const WalletContext = createContext<WalletContextType | null>(null);

export const WalletProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, refreshUser } = useAuth();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  
  // Use actual user data instead of dead state
  const balance = user?.walletBalance || 0;
  const pendingBalance = 0; // Transactions are processed immediately

  // Load wallet data
  const refreshWallet = useCallback(async () => {
    if (!user) return;
    
    logger.info('[WalletContext] Refreshing wallet data');
    setLoading(true);
    try {
      // Load user transactions if needed
      // For now, we refresh user data which includes wallet balance
      await refreshUser();
    } catch (error) {
      logger.error('[WalletContext] Error refreshing wallet', error instanceof Error ? error : new Error(String(error)));
    } finally {
      setLoading(false);
    }
  }, [user, refreshUser]);

  const deductWalletBalance = useCallback(async (amount: number): Promise<boolean> => {
    if (!user) {
      addToast('Debes iniciar sesión', 'error');
      return false;
    }
    
    try {
      await userRepository.updateWalletBalance(user.id, -amount);
      await refreshUser();
      addToast(`$${amount.toFixed(2)} descontados del saldo`, 'success');
      return true;
    } catch (error) {
      addToast('Error al descontar saldo', 'error');
      logger.error('[WalletContext] Error deducting from wallet', error instanceof Error ? error : new Error(String(error)));
      return false;
    }
  }, [user, refreshUser, addToast]);

  const addWalletBalance = useCallback(async (amount: number): Promise<boolean> => {
    if (!user) {
      addToast('Debes iniciar sesión', 'error');
      return false;
    }
    
    try {
      await userRepository.updateWalletBalance(user.id, amount);
      await refreshUser();
      addToast(`$${amount.toFixed(2)} agregados al saldo`, 'success');
      return true;
    } catch (error) {
      addToast('Error al agregar saldo', 'error');
      logger.error('[WalletContext] Error adding to wallet', error instanceof Error ? error : new Error(String(error)));
      return false;
    }
  }, [user, refreshUser, addToast]);

  const topUp = useCallback(async (amount: number): Promise<string> => {
    try {
      const result = await walletService.processTopUp(amount);
      logger.info(`[WalletContext] Top up initiated for $${amount}`);
      return result.initPoint || '';
    } catch (error) {
      logger.error('[WalletContext] Error initiating top up', error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  }, []);

  const value = useMemo(() => ({
    balance,
    pendingBalance,
    transactions,
    loading,
    deductWalletBalance,
    addWalletBalance,
    topUp,
    refreshWallet
  }), [
    balance,
    pendingBalance,
    transactions,
    loading,
    deductWalletBalance,
    addWalletBalance,
    topUp,
    refreshWallet
  ]);

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};

export default WalletContext;
